import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Link, Clock, ChevronDown } from '../../icons/wp-icons';
import { calendlyApi } from '../../api/calendlyApi';

const CalendlyLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="#006BFF" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.655 14.262c.281 0 .557.023.828.064 0 .005-.005.01-.005.014-.105.267-.234.534-.381.786l-1.219 2.106c-1.112 1.936-3.177 3.127-5.411 3.127h-2.432c-2.23 0-4.294-1.191-5.412-3.127l-1.218-2.106a6.251 6.251 0 0 1 0-6.252l1.218-2.106C6.736 4.832 8.8 3.641 11.035 3.641h2.432c2.23 0 4.294 1.191 5.411 3.127l1.219 2.106c.147.252.271.519.381.786 0 .004.005.009.005.014-.267.041-.543.064-.828.064-1.816 0-2.501-.607-3.291-1.306-.764-.676-1.711-1.517-3.44-1.517h-1.029c-1.251 0-2.387.455-3.2 1.278-.796.805-1.233 1.904-1.233 3.099v1.411c0 1.196.437 2.295 1.233 3.099.813.823 1.949 1.278 3.2 1.278h1.034c1.729 0 2.676-.841 3.439-1.517.791-.703 1.471-1.306 3.287-1.301Zm.005-3.237c.399 0 .794-.036 1.179-.11-.002-.004-.002-.01-.002-.014-.073-.414-.193-.823-.349-1.218.731-.12 1.407-.396 1.986-.819 0-.004-.005-.013-.005-.018-.331-1.085-.832-2.101-1.489-3.03-.649-.915-1.435-1.719-2.331-2.395-1.867-1.398-4.088-2.138-6.428-2.138-1.448 0-2.855.28-4.175.841-1.273.543-2.423 1.315-3.407 2.299S2.878 6.552 2.341 7.83c-.557 1.324-.842 2.726-.842 4.175 0 1.448.281 2.855.842 4.174.542 1.274 1.314 2.423 2.298 3.407s2.129 1.761 3.407 2.299c1.324.556 2.727.841 4.175.841 2.34 0 4.561-.74 6.428-2.137a10.815 10.815 0 0 0 2.331-2.396c.652-.929 1.158-1.949 1.489-3.03 0-.004.005-.014.005-.018-.579-.423-1.255-.699-1.986-.819.161-.395.276-.804.349-1.218.005-.009.005-.014.005-.023.869.166 1.692.506 2.404 1.035.685.505.552 1.075.446 1.416C22.184 20.437 17.619 24 12.221 24c-6.625 0-12-5.375-12-12s5.37-12 12-12c5.398 0 9.963 3.563 11.471 8.464.106.341.239.915-.446 1.421-.717.529-1.535.873-2.404 1.034.128.716.128 1.45 0 2.166-.387-.074-.782-.11-1.182-.11-4.184 0-3.968 2.823-6.736 2.823h-1.029c-1.899 0-3.15-1.357-3.15-3.095v-1.411c0-1.738 1.251-3.094 3.15-3.094h1.034c2.768 0 2.552 2.823 6.731 2.827Z"/>
  </svg>
);

function CalendlySettings({ readOnly = false }) {
  const [status, setStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [apiToken, setApiToken] = useState('');
  const [eventTypes, setEventTypes] = useState([]);
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const data = await calendlyApi.getStatus();
      setStatus(data);

      if (data.connected) {
        loadEventTypes();
      }
    } catch (err) {
      console.error('Calendly Status Fehler:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadEventTypes = async () => {
    try {
      setLoadingEventTypes(true);
      const data = await calendlyApi.getEventTypes();
      setEventTypes(data.eventTypes || []);
    } catch (err) {
      console.error('Event Types laden fehlgeschlagen:', err);
    } finally {
      setLoadingEventTypes(false);
    }
  };

  const handleConnect = async () => {
    if (!apiToken.trim()) {
      setError('Bitte API Token eingeben');
      return;
    }

    try {
      setConnecting(true);
      setError('');
      await calendlyApi.connect(apiToken.trim());
      setApiToken('');
      await checkStatus();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Verbindung fehlgeschlagen';
      setError(msg);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Calendly-Verbindung wirklich trennen?')) return;

    try {
      await calendlyApi.disconnect();
      setStatus({ connected: false });
      setEventTypes([]);
    } catch (err) {
      alert('Fehler beim Trennen: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSetDefaultEventType = async (uri) => {
    try {
      await calendlyApi.updateSettings({ defaultEventTypeUri: uri || null });
      setStatus(prev => ({ ...prev, defaultEventTypeUri: uri || null }));
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6 border-t-4" style={{ borderTopColor: '#006BFF' }}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-white border border-gray-200 flex items-center justify-center shadow-sm">
            <CalendlyLogo className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-semibold">Calendly</h2>
        </div>

        {status.connected ? (
          <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <CheckCircle className="w-4 h-4" />
            Verbunden
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            <XCircle className="w-4 h-4" />
            Nicht verbunden
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-[#EBF3FF] border border-[#B3D4FF] rounded-lg p-4 mb-6">
        <p className="text-sm text-[#0B3558]">
          <strong>Calendly-Integration:</strong> Ermöglicht Ihren Kunden, selbst Termine zu buchen.
        </p>
        <ul className="mt-2 text-sm text-[#004796] space-y-1 ml-4">
          <li>Kunden buchen selbst - Termin wird automatisch im CRM angelegt</li>
          <li>Stornierungen werden automatisch synchronisiert</li>
          <li>Einmal-Buchungslinks pro Kunde generierbar</li>
          <li>KI-Agent kann Buchungslinks in E-Mails einbetten</li>
        </ul>
      </div>

      {status.connected ? (
        <div className="space-y-4">
          {/* Verbindungsinfo */}
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-900 font-medium">
              Verbunden als {status.userName}
            </p>
            <p className="text-sm text-green-700 mt-1">
              {status.userEmail}
              {status.webhookAktiv && (
                <span className="ml-2 inline-flex items-center gap-1 text-green-600">
                  <Link className="w-3 h-3" /> Webhook aktiv
                </span>
              )}
            </p>
          </div>

          {/* Event Type Auswahl */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <label className="block text-sm font-medium text-text mb-2">
              Standard Event Type (für Buchungslinks)
            </label>
            {loadingEventTypes ? (
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                Lade Event Types...
              </div>
            ) : eventTypes.length > 0 ? (
              <div className="relative">
                <select
                  value={status.defaultEventTypeUri || ''}
                  onChange={(e) => handleSetDefaultEventType(e.target.value)}
                  disabled={readOnly}
                  className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#006BFF] focus:border-transparent disabled:opacity-50"
                >
                  <option value="">Kein Standard Event Type</option>
                  {eventTypes.map(et => (
                    <option key={et.uri} value={et.uri}>
                      {et.name} ({et.duration} Min.)
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            ) : (
              <p className="text-sm text-text-muted">
                Keine Event Types gefunden. Erstellen Sie zuerst einen in Calendly.
              </p>
            )}
            <p className="text-xs text-text-muted mt-1.5">
              Wird verwendet wenn der KI-Agent oder das CRM einen Buchungslink generiert.
            </p>
          </div>

          {!readOnly && (
            <button
              onClick={handleDisconnect}
              className="w-full px-4 py-3 bg-danger text-white rounded-button hover:bg-red-700 transition font-medium"
            >
              Verbindung trennen
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Token-Eingabe */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-text font-medium mb-3">
              Calendly verbinden
            </p>
            <p className="text-sm text-text-muted mb-4">
              {readOnly
                ? 'Sie haben keine Berechtigung, diese Einstellung zu ändern.'
                : (
                  <>
                    Erstellen Sie einen Personal Access Token in Ihren{' '}
                    <a
                      href="https://calendly.com/integrations/api_webhooks"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#006BFF] hover:underline"
                    >
                      Calendly-Einstellungen
                    </a>
                    {' '}und tragen Sie ihn hier ein.
                  </>
                )}
            </p>

            {!readOnly && (
              <>
                <input
                  type="password"
                  value={apiToken}
                  onChange={(e) => { setApiToken(e.target.value); setError(''); }}
                  placeholder="eyJraWQiOi..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#006BFF] focus:border-transparent"
                />

                {error && (
                  <p className="text-sm text-red-600 mb-3">{error}</p>
                )}

                <button
                  onClick={handleConnect}
                  disabled={connecting || !apiToken.trim()}
                  className="w-full px-4 py-3 bg-[#006BFF] text-white rounded-button hover:bg-[#004796] transition font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {connecting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Verbinde...
                    </>
                  ) : (
                    <>
                      <Link className="w-5 h-5" />
                      Mit Calendly verbinden
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendlySettings;
